import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, Button, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Layout from 'Component/Layouts/layout';
import useApiCaller from 'hooks/apiCaller';
import useCredentials from 'hooks/useCredentials';
import { ROLES } from 'Constants/roles';

const BatchTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const apiCaller = useApiCaller();
  const { userData: user } = useCredentials();

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      const payload = {
        employeeId: user?.employeeId,
      };

      const response = await apiCaller.post('/user/get-training-tasks', payload);
      console.log('response', response?.data?.data?.tasks);
      if (response?.data?.data?.tasks) {
        setTasks(response.data.data?.tasks);
      } else {
        setTasks([]);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setError('Failed to fetch tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTakeAction = (task) => {
    const { batchId } = task;

    if (!batchId) {
      console.error('No batch ID found for task:', task);
      return;
    }

    // Redirect based on user role
    if (user?.roleName === ROLES.RM) {
      navigate(`/trainee-approval/${batchId}`);
    } else if (user?.roleName === ROLES.CH || user?.roleName === ROLES.BH) {
      navigate(`/batch-approval/${batchId}`);
    } else {
      // For other roles, you might want to handle differently
      console.log('No specific action defined for role:', user?.roleName);
    }
  };

  const renderTasksGrid = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 2,
        mt: 2,
        px: 2,
      }}
    >
      {tasks.map((task) => (
        <TaskCard
          key={task.batchId || task.id}
          task={task}
          onClick={() => handleTakeAction(task)}
        />
      ))}
    </Box>
  );

  return (
    <Layout>
      <Box
        className="p-3"
        sx={{
          fontSize: 24,
          fontWeight: 500,
          background: 'linear-gradient(to bottom, #E9FBE0 0%, #E6F0E8 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Training Tasks
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      ) : tasks.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No training tasks found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You don't have any pending training tasks at the moment.
          </Typography>
        </Box>
      ) : (
        renderTasksGrid()
      )}
    </Layout>
  );
};

const TaskCard = ({ task, onClick }) => (
  <Card sx={{ borderRadius: 4, minHeight: 230 }}>
    <Box className="p-3">
      <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
        {task.batchName || task.taskName || `Batch ${task.batchId}`}
      </Typography>

      <Box
        className="mt-3"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Box>
          <Info label="Batch ID" value={task.batchId || 'N/A'} />
          <Info label="Deadline" value={formatDate(task.deadline)} />
        </Box>
        <Box>
          <Info label="Status" value={task.status || 'Pending'} mt />
        </Box>
        <Box sx={{ width: 60, height: 60 }} />
      </Box>

      {task.description && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 12, color: '#706C84' }}>Description</Typography>
          <Typography sx={{ fontSize: 12, color: '#110B2C' }}>{task.description}</Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          variant="contained"
          size="medium"
          sx={{ borderRadius: 50, background: '#2800B1', fontSize: 12 }}
          onClick={onClick}
        >
          Take Action
        </Button>
      </Box>
    </Box>
  </Card>
);

const Info = ({ label, value, mt }) => (
  <Box className={mt ? 'mt-4' : ''}>
    <Typography sx={{ fontSize: 12, color: '#706C84' }}>{label}</Typography>
    <Typography sx={{ fontSize: 12, color: '#110B2C' }}>{value}</Typography>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return isNaN(date) ? dateString : date.toLocaleDateString('en-GB');
};

export default BatchTasks;
